/**
 * useItemLock Hook
 * 
 * React hook for managing item locks in wizards/editors.
 * Provides:
 * - Auto-acquire lock when opening for edit
 * - Heartbeat to refresh lock every 30 seconds
 * - Auto-release on unmount/close
 * - Handle browser close/refresh (beforeunload)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { itemLockStore, LockStatus } from '@/lib/stores/itemLockStore';
import { getCurrentUser } from '@/lib/stores/unifiedUserStore';
import { ItemLock } from '@/lib/api-functions/api-service';
import { lockPersistenceManager } from '@/lib/locks/lockPersistenceManager';

// Lock heartbeat interval (30 seconds)
const HEARTBEAT_INTERVAL_MS = 30 * 1000;

export type ItemType = 'object' | 'dialog' | 'document' | 'template' | 'flow';

export interface UseItemLockOptions {
  /** Item ID to lock */
  itemId: string | undefined;
  /** Item type */
  itemType: ItemType;
  /** Game ID */
  gameId: string | undefined;
  /** Whether to automatically acquire lock on mount */
  autoAcquire?: boolean;
  /** Whether the item is in edit mode */
  isEditMode?: boolean;
  /** Callback when lock acquisition fails */
  onLockFailed?: (error: string, lockedBy?: string) => void;
  /** Callback when lock is acquired */
  onLockAcquired?: (lock: ItemLock) => void;
  /** Callback when lock is released */
  onLockReleased?: () => void;
  /** Callback when lock is stolen by another user */
  onLockLost?: () => void;
  /** Callback when lock becomes available (another user released it) - use this to refresh data */
  onLockBecameAvailable?: () => void;
}

export interface UseItemLockReturn {
  /** Current lock status */
  lockStatus: LockStatus;
  /** Whether this user can edit (owns lock or no lock) */
  canEdit: boolean;
  /** Whether lock acquisition is in progress */
  isAcquiring: boolean;
  /** Error message if lock acquisition failed */
  lockError: string | null;
  /** Whether the item is locked by another user */
  isLockedByOther: boolean;
  /** Manually acquire the lock */
  acquireLock: () => Promise<boolean>;
  /** Manually release the lock */
  releaseLock: () => Promise<boolean>;
  /** Force refresh lock status from server */
  refreshLockStatus: () => Promise<void>;
  /** Force acquire lock (kick another user) */
  forceLock: () => Promise<boolean>;
  /** Release lock immediately without persisting (for explicit tab close) */
  releaseImmediately: () => Promise<void>;
}

export function useItemLock({
  itemId,
  itemType,
  gameId,
  autoAcquire = true,
  isEditMode = false,
  onLockFailed,
  onLockAcquired,
  onLockReleased,
  onLockLost,
  onLockBecameAvailable,
}: UseItemLockOptions): UseItemLockReturn {
  const [lockStatus, setLockStatus] = useState<LockStatus>({
    isLocked: false,
    lockedBy: null,
    lockedByName: null,
    lockedByAvatar: null,
    lockedAt: null,
    expiresAt: null,
    isOwnLock: false,
  });
  // Start as acquiring if we're in edit mode with autoAcquire - this blocks editing until lock check completes
  const [isAcquiring, setIsAcquiring] = useState(autoAcquire && isEditMode && !!itemId);
  const [lockError, setLockError] = useState<string | null>(null);

  // Refs to track current state for callbacks
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasLockRef = useRef(false);
  const itemIdRef = useRef(itemId);
  const itemTypeRef = useRef(itemType);
  const gameIdRef = useRef(gameId);
  const isExplicitCloseRef = useRef(false); // Track if this is an explicit close vs navigation

  // Refs for callbacks to avoid stale closures and infinite loops
  const onLockFailedRef = useRef(onLockFailed);
  const onLockAcquiredRef = useRef(onLockAcquired);
  const onLockReleasedRef = useRef(onLockReleased);
  const onLockLostRef = useRef(onLockLost);
  const onLockBecameAvailableRef = useRef(onLockBecameAvailable);
  
  // Track if we were previously locked by someone else (for detecting when lock becomes available)
  const wasLockedByOtherRef = useRef(false);

  // Keep refs up to date
  useEffect(() => {
    itemIdRef.current = itemId;
    itemTypeRef.current = itemType;
    gameIdRef.current = gameId;
    onLockFailedRef.current = onLockFailed;
    onLockAcquiredRef.current = onLockAcquired;
    onLockReleasedRef.current = onLockReleased;
    onLockLostRef.current = onLockLost;
    onLockBecameAvailableRef.current = onLockBecameAvailable;
  }, [itemId, itemType, gameId, onLockFailed, onLockAcquired, onLockReleased, onLockLost, onLockBecameAvailable]);

  /**
   * Update lock status from store (stable callback - uses refs)
   */
  const updateLockStatus = useCallback(() => {
    if (!itemIdRef.current || !itemTypeRef.current) return;
    
    const status = itemLockStore.getLockStatus(itemIdRef.current, itemTypeRef.current);
    setLockStatus(status);

    // Check if we lost our lock
    if (hasLockRef.current && !status.isOwnLock && status.isLocked) {
      hasLockRef.current = false;
      onLockLostRef.current?.();
    }
  }, []); // No dependencies - uses refs for stability

  /**
   * Start heartbeat interval
   */
  const startHeartbeat = useCallback(() => {
    // Clear existing interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    // Start new interval
    heartbeatIntervalRef.current = setInterval(async () => {
      if (hasLockRef.current && itemIdRef.current && itemTypeRef.current) {
        const success = await itemLockStore.refreshLock(
          itemIdRef.current,
          itemTypeRef.current
        );
        if (!success) {
          console.warn('[useItemLock] Heartbeat failed - lock may be lost');
        }
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, []);

  /**
   * Stop heartbeat interval
   */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  /**
   * Initialize store and subscribe to changes
   */
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (!gameId || !currentUser) return;

    // Initialize the store
    itemLockStore.initialize(gameId, currentUser.id);

    // Subscribe to lock changes - use a stable callback
    const unsubscribe = itemLockStore.subscribe(() => {
      // Only update if we have valid item info
      if (itemIdRef.current && itemTypeRef.current) {
        const status = itemLockStore.getLockStatus(itemIdRef.current, itemTypeRef.current);
        const previouslyLockedByOther = wasLockedByOtherRef.current;
        const nowLockedByOther = status.isLocked && !status.isOwnLock;
        const nowUnlocked = !status.isLocked;
        
        setLockStatus(status);

        // Check if we lost our lock
        if (hasLockRef.current && !status.isOwnLock && status.isLocked) {
          hasLockRef.current = false;
          onLockLostRef.current?.();
        }

        // Check if lock became available (was locked by someone else, now unlocked)
        // This happens when the other user releases their lock
        if (previouslyLockedByOther && nowUnlocked && autoAcquire && isEditMode) {
          
          // Notify that lock became available (so component can refresh data)
          onLockBecameAvailableRef.current?.();
          
          // Try to acquire the lock
          setIsAcquiring(true);
          itemLockStore.acquireLock(itemIdRef.current, itemTypeRef.current, gameId).then((result) => {
            if (result.success && result.lock) {
              hasLockRef.current = true;
              updateLockStatus();
              onLockAcquiredRef.current?.(result.lock);
              startHeartbeat();
            } else {
              updateLockStatus();
            }
            setIsAcquiring(false);
          });
        }

        // Update tracking ref
        wasLockedByOtherRef.current = nowLockedByOther;
      }
    });

    // Initial update and set initial tracking state
    updateLockStatus();
    const initialStatus = itemLockStore.getLockStatus(itemIdRef.current || '', itemTypeRef.current);
    wasLockedByOtherRef.current = initialStatus.isLocked && !initialStatus.isOwnLock;

    return () => {
      unsubscribe();
    };
  }, [gameId, autoAcquire, isEditMode, updateLockStatus, startHeartbeat]); // Added dependencies for auto-acquire logic

  /**
   * Acquire lock (stable callback - uses refs)
   */
  const acquireLock = useCallback(async (): Promise<boolean> => {
    const currentItemId = itemIdRef.current;
    const currentItemType = itemTypeRef.current;
    const currentGameId = gameIdRef.current;

    if (!currentItemId || !currentGameId) {
      setLockError('Missing item ID or game ID');
      setIsAcquiring(false);
      return false;
    }

    setIsAcquiring(true);
    setLockError(null);

    try {
      const result = await itemLockStore.acquireLock(currentItemId, currentItemType, currentGameId);

      if (result.success && result.lock) {
        hasLockRef.current = true;
        
        updateLockStatus();
        onLockAcquiredRef.current?.(result.lock);
        return true;
      } else {
        const errorMessage = result.error || 'Failed to acquire lock';
        setLockError(errorMessage);
        
        // Update lock status immediately - the store should now have the lock info from the 409 response
        updateLockStatus();
        
        const currentStatus = itemLockStore.getLockStatus(currentItemId, currentItemType);
        onLockFailedRef.current?.(errorMessage, currentStatus.lockedByName || undefined);
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setLockError(errorMessage);
      onLockFailedRef.current?.(errorMessage);
      return false;
    } finally {
      setIsAcquiring(false);
    }
  }, [updateLockStatus]); // Only depends on updateLockStatus which is stable

  /**
   * Release lock (stable callback - uses refs)
   */
  const releaseLock = useCallback(async (): Promise<boolean> => {
    const currentItemId = itemIdRef.current;
    const currentItemType = itemTypeRef.current;

    if (!currentItemId) return false;

    try {
      const success = await itemLockStore.releaseLock(currentItemId, currentItemType);
      if (success) {
        hasLockRef.current = false;
        updateLockStatus();
        onLockReleasedRef.current?.();
      }
      return success;
    } catch (error) {
      console.error('[useItemLock] Failed to release lock:', error);
      return false;
    }
  }, [updateLockStatus]); // Only depends on updateLockStatus which is stable

  /**
   * Refresh lock status from server
   */
  const refreshLockStatus = useCallback(async (): Promise<void> => {
    await itemLockStore.refresh();
    updateLockStatus();
  }, [updateLockStatus]);

  /**
   * Force acquire lock (kick another user)
   */
  const forceLock = useCallback(async (): Promise<boolean> => {
    const currentItemId = itemIdRef.current;
    const currentItemType = itemTypeRef.current;
    const currentGameId = gameIdRef.current;

    if (!currentItemId || !currentGameId) {
      setLockError('Missing item ID or game ID');
      return false;
    }

    setIsAcquiring(true);
    setLockError(null);

    try {
      const result = await itemLockStore.forceLock(currentItemId, currentItemType, currentGameId);

      if (result.success && result.lock) {
        hasLockRef.current = true;
        updateLockStatus();
        onLockAcquiredRef.current?.(result.lock);
        startHeartbeat();
        return true;
      } else {
        const errorMessage = result.error || 'Failed to force acquire lock';
        setLockError(errorMessage);
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[useItemLock] Force lock acquisition error for ${currentItemType}:${currentItemId}:`, error);
      setLockError(errorMessage);
      return false;
    } finally {
      setIsAcquiring(false);
    }
  }, [updateLockStatus, startHeartbeat]);

  /**
   * Handle beforeunload event to release all persisted locks on page close
   */
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Release all persisted locks on browser tab close
      const persistedLocks = lockPersistenceManager.getStoredLocks();
      const token = localStorage.getItem('auth_token');
      
      if (token) {
        const API_BASE = import.meta.env.VITE_API_URL || 'https://locanest-api.vercel.app';
        
        // Release current lock if we have it
        if (hasLockRef.current && itemIdRef.current && itemTypeRef.current) {
          const params = new URLSearchParams({
            item_id: itemIdRef.current,
            item_type: itemTypeRef.current,
          });
          
          // Use fetch with keepalive instead of sendBeacon for better reliability
          // keepalive allows the request to outlive the page
          fetch(`${API_BASE}/api/item-locks?${params.toString()}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              _method: 'DELETE', // Method override for API compatibility
            }),
            keepalive: true, // Critical: keeps request alive after page unload
          }).catch((err) => {
            console.error('[useItemLock] Failed to release lock on beforeunload:', err);
          });
        }
        
        // Release all other persisted locks
        persistedLocks.forEach(lock => {
          // Skip if it's the current lock (already handled above)
          if (lock.itemId === itemIdRef.current && lock.itemType === itemTypeRef.current) {
            return;
          }
          
          const params = new URLSearchParams({
            item_id: lock.itemId,
            item_type: lock.itemType,
          });
          
          // Use fetch with keepalive for persisted locks too
          fetch(`${API_BASE}/api/item-locks?${params.toString()}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              _method: 'DELETE',
            }),
            keepalive: true,
          }).catch((err) => {
            console.error('[useItemLock] Failed to release persisted lock:', err);
          });
        });
        
        // Clear all persisted locks
        lockPersistenceManager.clearAll();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  /**
   * Auto-acquire lock when entering edit mode
   */
  useEffect(() => {
    const currentItemId = itemIdRef.current;
    const currentItemType = itemTypeRef.current;
    const currentGameId = gameIdRef.current;


    if (autoAcquire && isEditMode && currentItemId && currentGameId) {
      // Check if we have a persisted lock for this item
      const persistedLock = lockPersistenceManager.getStoredLock(currentItemId, currentItemType);
      
      if (persistedLock) {
        // We have a persisted lock, verify it's still valid on the server
        
        // Try to refresh the lock to verify it still exists
        itemLockStore.refreshLock(currentItemId, currentItemType).then((refreshSuccess) => {
          if (refreshSuccess) {
            // Lock still valid, restore it

            hasLockRef.current = true;
            startHeartbeat();
            setIsAcquiring(false);
            // Remove from persistence now that we've restored it (global heartbeat will keep it alive)
            lockPersistenceManager.removeLock(currentItemId, currentItemType);
          } else {
            // Lock expired or doesn't exist, remove from persistence and acquire new one
            lockPersistenceManager.removeLock(currentItemId, currentItemType);
            
            // Try to acquire new lock
            acquireLock().then((success) => {
              if (success) {
                startHeartbeat();
              }
            });
          }
        });
        
        return; // Exit early, async verification will handle the rest
      }
      
      // Check if we already have the lock
      const status = itemLockStore.getLockStatus(currentItemId, currentItemType);
      
      if (!status.isOwnLock) {
        acquireLock().then((success) => {
          if (success) {
            startHeartbeat();
          }
        });
      } else {
        // Already have lock, just start heartbeat
        hasLockRef.current = true;
        startHeartbeat();
        setIsAcquiring(false); // We already have the lock, so we're not acquiring
      }
    } else if (!isEditMode || !autoAcquire) {
      // Not in edit mode or autoAcquire is off - not acquiring
      setIsAcquiring(false);
    }

    // NOTE: We don't release the lock in this cleanup anymore.
    // The lock is only released on component unmount (see below).
    // This prevents accidental lock releases when effect dependencies change.
    return () => {
      // Only stop heartbeat if leaving edit mode, but DON'T release the lock here
      // Lock release is handled in the unmount effect
      if (!isEditMode) {
        stopHeartbeat();
      }
    };
  }, [autoAcquire, isEditMode, itemId, gameId, acquireLock, startHeartbeat, stopHeartbeat]);

  /**
   * Cleanup on unmount - either release immediately or store for persistence
   * Uses refs to get the current values at cleanup time, not at effect creation time
   */
  useEffect(() => {
    return () => {
      // Use refs to get current values at cleanup time
      const currentItemId = itemIdRef.current;
      const currentItemType = itemTypeRef.current;
      const currentGameId = gameIdRef.current;
      const isExplicitClose = isExplicitCloseRef.current;
      
      stopHeartbeat(); // Stop local heartbeat
      
      if (hasLockRef.current && currentItemId) {
        if (isExplicitClose) {
          // Explicit close (e.g., tab closed) - release lock immediately
          itemLockStore.releaseLock(currentItemId, currentItemType);
          lockPersistenceManager.removeLock(currentItemId, currentItemType);
          hasLockRef.current = false;
        } else if (currentGameId) {
          // Navigation away - persist lock for later restoration
          lockPersistenceManager.storeLock(currentItemId, currentItemType, currentGameId);
          // Don't release the lock - global heartbeat will keep it alive
        }
      }
    };
  }, []); // Empty deps - only runs on unmount

  /**
   * Release lock immediately without persisting (for explicit tab close)
   */
  const releaseImmediately = useCallback(async (): Promise<void> => {
    const currentItemId = itemIdRef.current;
    const currentItemType = itemTypeRef.current;

    if (!currentItemId) return;

    
    // Mark as explicit close so unmountP cleanup releases instead of persisting
    isExplicitCloseRef.current = true;
    
    // Stop heartbeat and release lock
    stopHeartbeat();
    if (hasLockRef.current) {
      await itemLockStore.releaseLock(currentItemId, currentItemType);
      lockPersistenceManager.removeLock(currentItemId, currentItemType);
      hasLockRef.current = false;
      onLockReleasedRef.current?.();
    }
  }, [stopHeartbeat]);

  // Compute derived state
  // Block editing while acquiring lock OR if locked by someone else
  const canEdit = !isAcquiring && (!lockStatus.isLocked || lockStatus.isOwnLock);
  const isLockedByOther = lockStatus.isLocked && !lockStatus.isOwnLock;

  return {
    lockStatus,
    canEdit,
    isAcquiring,
    lockError,
    isLockedByOther,
    acquireLock,
    releaseLock,
    refreshLockStatus,
    forceLock,
    releaseImmediately,
  };
}

export default useItemLock;

